"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NotificationBanner = NotificationBanner;
var jsx_runtime_1 = require("react/jsx-runtime");
// ================================================================
// v2/components/NotificationBanner/index.tsx
// イベント詳細上部の「お知らせ」バナー。GlassCard を外殻に使い、
// ヘッダ「お知らせ N件」+ 通知行 (NotificationItem) を縦に積む。
//
// - 外殻 glass / shadow / accentBar はすべて GlassCard に委譲する
//   (自前で glass の sx は書かない)。
// - 行内のアイコン丸背景・前景色・締切色はすべて theme.palette.v2
//   (useV2Palette) から取得。色のハードコードはしない。
//
// NotificationItem は本バナー専用の内部行コンポーネント。別 barrel は
// 持たず、ここで定義して NotificationBanner からのみ利用する。
// ================================================================
var material_1 = require("@mui/material");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
var CountdownText_1 = require("../CountdownText");
var GlassCard_1 = require("../GlassCard");
var Icons_1 = require("../Icons");
var Tappable_1 = require("../internal/Tappable");
function NotificationItem(_a) {
    var iconVariant = _a.iconVariant, title = _a.title, statusLabel = _a.statusLabel, countdown = _a.countdown, countdownVariant = _a.countdownVariant, href = _a.href, onClick = _a.onClick, showTopBorder = _a.showTopBorder;
    var p = (0, theme_1.useV2Palette)();
    // 丸アイコンの背景色 / 前景色をトークンから解決する。
    //   calendar: 背景 background.info.subtle    / アイコン foreground.semantic.info
    //   clock   : 背景 background.warning.subtle / アイコン foreground.semantic.warning
    var circle = iconVariant === 'clock'
        ? {
            Icon: Icons_1.ClockIcon,
            bg: p.background.warning.subtle,
            color: p.foreground.semantic.warning,
        }
        : {
            Icon: Icons_1.CalendarIcon,
            bg: p.background.info.subtle,
            color: p.foreground.semantic.info,
        };
    var Icon = circle.Icon;
    return (
    // 行全体をタップ領域にする。component は Tappable 側で意図的に渡していない
    // (theme の LinkComponent = react-router へ差し替えさせるため)
    (0, jsx_runtime_1.jsxs)(Tappable_1.Tappable, { href: href, onClick: onClick, sx: {
            display: 'flex',
            alignItems: 'center',
            gap: "".concat(tokens_1.spacing.sm, "px"),
            px: "".concat(tokens_1.spacing.md, "px"),
            height: 64,
            width: '100%',
            textAlign: 'left',
            textDecoration: 'none',
            borderTop: showTopBorder
                ? "1px solid ".concat(p.border.neutral.default)
                : 'none',
            transition: 'background-color 0.15s ease',
            '@media (hover: hover)': {
                '&:hover': { backgroundColor: p.background.overlayHover },
            },
        }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                    width: 32,
                    height: 32,
                    borderRadius: "".concat(tokens_1.radius.full, "px"),
                    backgroundColor: circle.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }, children: (0, jsx_runtime_1.jsx)(Icon, { sx: { fontSize: 16, color: circle.color } }) }), (0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "column", spacing: "".concat(tokens_1.spacing.xs, "px"), sx: { flexGrow: 1, minWidth: 0 }, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                            fontSize: tokens_1.fontSize.xs,
                            fontWeight: tokens_1.fontWeight.semiBold,
                            lineHeight: "".concat(tokens_1.lineHeight.xs, "px"),
                            color: p.foreground.neutral.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }, children: title }), (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                            fontSize: tokens_1.fontSize['2xs'],
                            fontWeight: tokens_1.fontWeight.semiBold,
                            lineHeight: "".concat(tokens_1.lineHeight['2xs'], "px"),
                            color: p.foreground.neutral.secondary,
                            whiteSpace: 'nowrap',
                        }, children: statusLabel })] }), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { flexShrink: 0 }, children: (0, jsx_runtime_1.jsx)(CountdownText_1.CountdownText, { value: countdown, variant: countdownVariant }) }), (0, jsx_runtime_1.jsx)(Icons_1.ChevronRightIcon, { sx: {
                    fontSize: 16,
                    color: p.foreground.neutral.secondary,
                    flexShrink: 0,
                } })] }));
}
function NotificationBanner(_a) {
    var headerLabel = _a.headerLabel, items = _a.items;
    var p = (0, theme_1.useV2Palette)();
    return (
    // 外殻の glass / shadow / 左アクセントバーは GlassCard に委譲。
    (0, jsx_runtime_1.jsxs)(GlassCard_1.GlassCard, { accentBar: true, cornerRadius: tokens_1.radius.md, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                    px: "".concat(tokens_1.spacing.md, "px"),
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: "1px solid ".concat(p.border.neutral.default),
                }, children: (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                        fontSize: tokens_1.fontSize.xs,
                        fontWeight: tokens_1.fontWeight.semiBold,
                        lineHeight: "".concat(tokens_1.lineHeight.xs, "px"),
                        color: p.foreground.neutral.secondary,
                    }, children: headerLabel }) }), items.map(function (item, index) { return ((0, jsx_runtime_1.jsx)(NotificationItem, __assign({}, item, { showTopBorder: index > 0 }), item.id)); })] }));
}
